/**
 * @prettier
 */
import { makeStyles } from '@material-ui/core/styles';
import { Button, Table } from '@pidedirecto/ui';
import { Form, FormCurrencyNumberStringField, FormHiddenField } from '@pidedirecto/ui/form';
import { useForm } from '@pidedirecto/ui/hooks';
import { useEffect } from 'react';
import * as React from 'react';
import { useFieldArray } from 'react-hook-form';
import { CashRegisterExpectedAmountVm } from 'src/api/letseatmanager/pos/getCashRegisterExpectedAmountsApi';
import { App } from 'src/constants/App';
import { RolePermissions } from 'src/constants/RolePermission';
import { translate } from 'src/i18n/translate';
import { posReducer } from 'src/reducers/posReducer';
import { useFormatAsRestaurantCurrencyNumber } from 'src/services/restaurant/useFormatAsRestaurantCurrencyNumber';
import { isCashPayment } from 'src/utils/paymentMethod/isCashPayment';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';
import { useUserHasRolePermission } from 'src/utils/react/useUserHasRolePermissions';
import { useIsSmallScreen } from 'src/utils/react/window/useIsSmallScreen';
import { emptyStringToUndefined } from 'src/utils/string/emptyStringToUndefined';

export function CashRegisterExpectedAmountReport({ title, expectedAmounts, loading, onSubmit }: Props): React.ReactElement {
    const classes = useStyles();
    const form = useForm();
    const isSmallScreen = useIsSmallScreen();
    const [userHasRolePermission] = useUserHasRolePermission();
    const formatAsCurrencyNumber = useFormatAsRestaurantCurrencyNumber();

    const { control } = form;
    const { fields, append } = useFieldArray({
        control,
        name: 'cashRegisterReport',
    });

    const posOnlyCashInCashRegisterReportEnabled = useSelector((state) => state.pos.context?.posOnlyCashInCashRegisterReportEnabled);
    const currency = useSelector((state) => state.app.restaurant?.currencyFormat?.currency);
    const country = useSelector((state) => state.app.restaurant?.country);
    const usedAppsAtThePosBusinessDayReports = useSelector((state) => state.pos.context?.usedChannelsAtThePosBusinessDayReport);

    const closeCashRegisterReport = useAction(posReducer.actions.closeCashRegisterReport);

    const hideExpectedAmount = userHasRolePermission(RolePermissions.BLIND_CASH_REGISTER_SHIFT);

    useEffect(() => {
        expectedAmounts?.forEach((report: CashRegisterExpectedAmountVm) => {
            if (!posOnlyCashInCashRegisterReportEnabled) return append({ paymentMethod: report.paymentMethod, customPaymentMethod: report.customPaymentMethod, amount: '' });

            if (isCashPayment(report.paymentMethod) && !report.customPaymentMethod) return append({ paymentMethod: report.paymentMethod, amount: '' });
        });
    }, [expectedAmounts]);

    let columns = [
        {
            id: 'paymentMethodAmount',
            content: translate('Payment Method'),
        },
        {
            id: 'expectedAmount',
            content: translate('Expected Amount'),
        },
    ];

    if (!isSmallScreen) {
        columns = [
            ...columns,
            {
                id: 'difference',
                content: translate('Difference'),
            },
        ];
    }

    return (
        <Form form={form} onSubmit={onSubmit} classes={{ form: classes.content }}>
            <section className={classes.titleContainer}>
                <h1 className={classes.title}>{title}</h1>
                <span className={classes.subTitle}>{translate("Remember once you closed the cash register you won't be able to create orders in the pos.")}</span>
                <span className={classes.subTitle}>{translate('Channels included in the count:')}</span>
                <span className={classes.subTitle}>
                    <strong>{usedAppsAtThePosBusinessDayReports ? usedAppsAtThePosBusinessDayReports?.map((app: App) => translate(`Apps.${app}`)).join(', ') : translate('All')}</strong>
                </span>

                <div className={classes.buttonsContainer}>
                    <Button variant='secondary' disabled={loading} onClick={closeCashRegisterReport}>
                        {translate('Cancel')}
                    </Button>
                    <Button type='submit' disabled={loading}>
                        {translate('Accept')}
                    </Button>
                </div>
            </section>
            <section className={classes.tableContainer}>
                <Table
                    columns={columns}
                    rows={fields?.map((report: any, idx: any) => {
                        const expectedPayment = expectedAmounts?.find(
                            (expectedAmount: CashRegisterExpectedAmountVm) =>
                                expectedAmount.paymentMethod === report?.paymentMethod &&
                                emptyStringToUndefined(expectedAmount.customPaymentMethod) === emptyStringToUndefined(report?.customPaymentMethod)
                        );
                        const hideExpectedCashAmount =
                            userHasRolePermission(RolePermissions.BLIND_CASH_REGISTER_SHIFT_FOR_CASH_ONLY) && isCashPayment(expectedPayment?.customPaymentMethod ?? expectedPayment?.paymentMethod);

                        return {
                            paymentMethodAmount: (
                                <div>
                                    <FormHiddenField name={`cashRegisterReport.${idx}.paymentMethod`} defaultValue={report.paymentMethod} />
                                    <FormHiddenField name={`cashRegisterReport.${idx}.customPaymentMethod`} defaultValue={report.customPaymentMethod} />
                                    <FormHiddenField name={`cashRegisterReport.${idx}.id`} defaultValue={report.id} />
                                    <FormCurrencyNumberStringField
                                        name={`cashRegisterReport.${idx}.amount`}
                                        label={translate(expectedPayment?.customPaymentMethod ?? expectedPayment?.paymentMethod)}
                                        placeholder={'0'}
                                        required
                                        disabled={loading}
                                        country={country}
                                    />
                                </div>
                            ),
                            expectedAmount:
                                hideExpectedCashAmount || hideExpectedAmount ? (
                                    '***'
                                ) : (
                                    <div className={classes.expectedAmount}>
                                        <div>{formatAsCurrencyNumber(expectedPayment?.amount, currency)}</div>
                                    </div>
                                ),
                            difference: '***',
                        };
                    })}
                />
            </section>
        </Form>
    );
}

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: theme.typography.semiBold,
        fontSize: 22,
        color: theme.palette.text.brand,
        [theme.breakpoints.up('sm')]: {
            fontSize: 26,
        },
    },
    expectedAmount: {
        fontFamily: theme.typography.regular,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    subTitle: {
        fontFamily: theme.typography.regular,
        color: theme.palette.text.secondary,
        fontSize: 16,
        [theme.breakpoints.up('sm')]: {
            fontSize: 18,
        },
        display: 'block',
        marginBottom: 10,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 15,
        justifyContent: 'center',
        alignItems: 'center',
        margin: '20px 0',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
        },
    },
    button: {
        minWidth: 280,
        [theme.breakpoints.up('sm')]: {
            minWidth: 'unset',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('lg')]: {
            flexDirection: 'row',
        },
    },
    titleContainer: {
        width: '100%',
        padding: 20,
        [theme.breakpoints.up('sm')]: {
            width: '80%',
            margin: '0 auto',
        },
        [theme.breakpoints.up('md')]: {
            width: '40%',
        },
    },
    tableContainer: {
        width: '90%',
        margin: '0 auto',
        [theme.breakpoints.up('sm')]: {
            width: '65%',
        },
    },
}));

type Props = {
    title: string;
    expectedAmounts: Array<CashRegisterExpectedAmountVm>;
    loading: boolean;
    onSubmit: any;
};
